import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem as MuiMenuItem,
  Divider,
  Drawer,
  useMediaQuery,
} from "@mui/material";
import GroupIcon from "@mui/icons-material/Group";
import DashboardIcon from "@mui/icons-material/Dashboard";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import BuildingIcon from "@mui/icons-material/Apartment";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CampaignIcon from "@mui/icons-material/Campaign";
import AdUnitsIcon from "@mui/icons-material/AdUnits";
import PaymentIcon from "@mui/icons-material/Payment";
import LinkIcon from "@mui/icons-material/Link";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { useTheme } from "@mui/material/styles";
import { connect } from "react-redux";
import { Types } from "../../constants/actionTypes";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import { apiFlagIsTrue, shouldShowCommunityJoinRequests } from "../../Utils/func";

// Sidebar Menu Item Component
const SidebarMenuItem = ({ icon: Icon, label, path, active, onClick, compact, danger }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: compact ? 0.75 : 1.25,
        mx: 1.5,
        borderRadius: "8px",
        cursor: "pointer",
        transition: "all 0.2s ease",
        backgroundColor: active
          ? isDark
            ? "rgba(99, 102, 241, 0.15)"
            : "rgba(25, 118, 210, 0.08)"
          : "transparent",
        color: danger
          ? "#ef4444"
          : active
          ? isDark
            ? "#818cf8"
            : "#1976d2"
          : isDark
          ? "#cbd5e1"
          : "#4a5568",
        "&:hover": {
          backgroundColor: danger
            ? isDark
              ? "rgba(239, 68, 68, 0.1)"
              : "#fef2f2"
            : active
            ? isDark
              ? "rgba(99, 102, 241, 0.2)"
              : "rgba(25, 118, 210, 0.12)"
            : isDark
            ? "rgba(255, 255, 255, 0.05)"
            : "#f7fafc",
          color: danger
            ? "#ef4444"
            : active
            ? isDark
              ? "#a5b4fc"
              : "#1565c0"
            : isDark
            ? "#ffffff"
            : "#1a202c",
          transform: "translateX(4px)",
        },
      }}
    >
      <Icon sx={{ fontSize: compact ? 18 : 20 }} />
      <Typography
        sx={{
          fontSize: compact ? "0.8rem" : "0.875rem",
          fontWeight: active ? 600 : 500,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

// Section Header
const SectionHeader = ({ title, compact }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Typography
      sx={{
        fontSize: "0.6875rem",
        fontWeight: 700,
        color: isDark ? "#718096" : "#718096",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        px: 3,
        py: compact ? 0.75 : 1.5,
        mt: compact ? 0.5 : 1,
      }}
    >
      {title}
    </Typography>
  );
};

const Sidebar = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mobileOpen = false, onMobileClose } = props;
  const navigate = useNavigate();
  const pathname = window.location.pathname;
  const profile = props?.profile || {};
  const userType = profile?.user_type || profile?.data?.user_type;
  const userEmail = profile?.email || profile?.data?.email || "user@sportsapp.com";
  const firstName = profile?.first_name || profile?.data?.first_name || "";
  const lastName = profile?.last_name || profile?.data?.last_name || "";
  const orgName =
    profile?.organisationDetails?.name ||
    profile?.data?.organisationDetails?.name ||
    profile?.organisation_name ||
    profile?.data?.organisation_name ||
    "";
  const userName = orgName || [firstName, lastName].filter(Boolean).join(" ") || "User";
  const profileImage =
    profile?.profile_image ||
    profile?.data?.profile_image ||
    profile?.logo_url ||
    profile?.data?.logo_url ||
    profile?.organisationDetails?.logo_url ||
    "";
  const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
  const [analyticsClubPath, setAnalyticsClubPath] = useState("/master-org/sub-orgs");
  const canSendAlerts = apiFlagIsTrue(
    profile?.can_send_alerts ?? profile?.data?.can_send_alerts
  );

  useEffect(() => {
    if (userType !== "master_org" || !profile?.token) return;
    (async () => {
      const data = await ApiServices.GetApiCall(ApiEndpoint.MASTER_ORG_SUB_ORGS, {
        "Content-Type": "application/json",
        "x-access-token": profile.token,
      });
      if (data?.status && Array.isArray(data.data) && data.data.length > 0) {
        setAnalyticsClubPath(`/master-org/clubs/${data.data[0].id}/content`);
      }
    })();
  }, [userType, profile?.token]);

  const handleLogout = () => {
    props.save_user_data({ user: "" });
    navigate("/");
  };

  const handleProfileMenuOpen = (event) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleProfileClick = () => {
    navigate(userType === "admin" ? "/user" : "/master-org/account");
    handleProfileMenuClose();
  };

  // Admin menu items
  const adminMenuItems = [
    { icon: DashboardIcon, label: "Home", path: "/dashboard" },
    { icon: GroupIcon, label: "Users", path: "/user" },
    { icon: StorefrontIcon, label: "Clubs", path: "/organizations" },
    { icon: CorporateFareIcon, label: "Club Partners", path: "/master-orgs" },
    { icon: BuildingIcon, label: "Connected Clubs", path: "/sub-orgs" },
    { icon: CampaignIcon, label: "Alerts", path: "/alerts" },
    { icon: PaymentIcon, label: "Payments", path: "/payments" },
    { icon: AdUnitsIcon, label: "Advertisements", path: "/advertisement" },
  ];

  // Club Partner menu items
  const masterOrgMenuItems = [
    { icon: DashboardIcon, label: "Home", path: "/master-org/dashboard" },
    ...(shouldShowCommunityJoinRequests(profile)
      ? [{ icon: GroupAddIcon, label: "Join Requests", path: "/master-org/join-requests" }]
      : []),
    { icon: LinkIcon, label: "Connect with Club", path: "/master/connect" },
    { icon: BuildingIcon, label: "Clubs", path: "/master-org/sub-orgs" },
    { icon: ShowChartIcon, label: "Analytics", path: analyticsClubPath, openAnalyticsTab: true },
    { icon: PeopleOutlineIcon, label: "Members", path: "/master-org/users" },
    ...(canSendAlerts
      ? [{ icon: CampaignIcon, label: "Alerts", path: "/master-org/alerts" }]
      : []),
    { icon: AdUnitsIcon, label: "Advertisements", path: "/master-org/advertisements" },
    { icon: SettingsIcon, label: "Settings", path: "/master-org/account" },
    { icon: LogoutIcon, label: "Logout", path: "__logout__", isLogout: true },
  ];

  const menuItems = userType === "admin" ? adminMenuItems : masterOrgMenuItems;

  const isMenuItemActive = (item) => {
    if (item.isLogout) return false;
    if (item.openAnalyticsTab) {
      return /^\/master-org\/clubs\/\d+\/content$/.test(pathname);
    }
    return pathname === item.path;
  };

  const handleMenuItemClick = (item) => {
    if (item.isLogout) {
      handleLogout();
      if (isMobile && onMobileClose) onMobileClose();
      return;
    }
    if (item.openAnalyticsTab && item.path.includes("/clubs/")) {
      navigate(item.path, { state: { initialTab: 1 } });
    } else {
      navigate(item.path);
    }
    if (isMobile && onMobileClose) onMobileClose();
  };

  const boxSx = {
    width: 280,
    height: "100vh",
    backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
    borderRight: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    left: 0,
    top: 0,
    zIndex: 1200,
    boxShadow: isDark ? "4px 0 12px rgba(0, 0, 0, 0.5)" : "4px 0 12px rgba(0, 0, 0, 0.08)",
  };

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { ...boxSx, boxSizing: "border-box", top: 56, height: "calc(100% - 56px)" },
        }}
      >
        <Box sx={{ width: 280, height: "100%", display: "flex", flexDirection: "column", backgroundColor: boxSx.backgroundColor }}>
          {/* Brand header — skip for master_org (org logo/name already in TopBar) */}
          {userType !== "master_org" && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar src={profileImage} sx={{ width: 32, height: 32, bgcolor: isDark ? "#6366f1" : "#1976d2", fontSize: "0.75rem" }}>{userName?.[0]?.toUpperCase() || "U"}</Avatar>
              <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, lineHeight: 1, color: isDark ? "#f8fafc" : "#0f172a", fontFamily: "'Inter', sans-serif" }}>SPORTS APP</Typography>
                <Typography sx={{ fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.15em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#64748b", mt: 0.25 }}>Admin</Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ flex: 1, py: 1, overflowY: "auto" }}>
            <SectionHeader title={userType === "admin" ? "Management" : "Dashboard"} compact />
            {menuItems.map((item, i) => (
              <SidebarMenuItem
                key={i}
                icon={item.icon}
                label={item.label}
                path={item.path}
                active={isMenuItemActive(item)}
                danger={item.isLogout}
                compact
                onClick={() => handleMenuItemClick(item)}
              />
            ))}
          </Box>
          {/* Profile footer — admin only (master_org uses top-left logo for settings) */}
          {userType === "admin" && (
            <Box sx={{ borderTop: isDark ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid #e2e8f0", p: 1.5 }}>
              <Box onClick={handleProfileMenuOpen} sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, borderRadius: "8px", cursor: "pointer", backgroundColor: profileMenuAnchor ? (isDark ? "rgba(255,255,255,0.1)" : "#f7fafc") : "transparent" }}>
                <Box component="img" src={isDark ? "/white.png" : "/black.png"} alt="SportsApp Logo" sx={{ height: 32, width: 32, objectFit: "contain", flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: isDark ? "#fff" : "#1a202c", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userName}</Typography>
                  <Typography sx={{ fontSize: "0.7rem", color: "#718096", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{userEmail}</Typography>
                </Box>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleProfileMenuOpen(e); }}><MoreVertIcon fontSize="small" /></IconButton>
              </Box>
              <Menu anchorEl={profileMenuAnchor} open={Boolean(profileMenuAnchor)} onClose={handleProfileMenuClose} anchorOrigin={{ vertical: "top", horizontal: "right" }} transformOrigin={{ vertical: "bottom", horizontal: "right" }} PaperProps={{ sx: { mt: -1, ml: -2, minWidth: 200, borderRadius: "12px", boxShadow: isDark ? "0 10px 40px rgba(0,0,0,0.5)" : "0 10px 40px rgba(0,0,0,0.15)", backgroundColor: isDark ? "#1a1a1a" : "#fff", border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" } }}>
                <MuiMenuItem onClick={() => { navigate("/admin/account"); handleProfileMenuClose(); }} sx={{ fontSize: "0.875rem", py: 1.5 }}><SettingsIcon sx={{ fontSize: 18, mr: 1.5, color: isDark ? "#818cf8" : "#1976d2" }} /> Account Settings</MuiMenuItem>
                <Divider sx={{ borderColor: isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0", my: 0.5 }} />
                <MuiMenuItem onClick={handleLogout} sx={{ fontSize: "0.875rem", color: "#ef4444", py: 1.5 }}><LogoutIcon sx={{ fontSize: 18, mr: 1.5 }} /> Logout</MuiMenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Drawer>
    );
  }

  return (
    <Box sx={{ ...boxSx, top: "72px", height: "calc(100vh - 72px)" }}>
      {/* Navigation */}
      <Box sx={{ flex: 1, py: 2, overflowY: "auto" }}>
        <SectionHeader title={userType === "admin" ? "Management" : "Dashboard"} />

        {menuItems.map((item, index) => (
          <SidebarMenuItem
            key={index}
            icon={item.icon}
            label={item.label}
            path={item.path}
            active={isMenuItemActive(item)}
            danger={item.isLogout}
            onClick={() => handleMenuItemClick(item)}
          />
        ))}
      </Box>

      {/* Profile footer — admin only (master_org uses top-left logo for settings) */}
      {userType === "admin" && (
        <Box
          sx={{
            borderTop: isDark
              ? "1px solid rgba(255, 255, 255, 0.05)"
              : "1px solid #e2e8f0",
            p: 2,
          }}
        >
          <Box
            onClick={handleProfileMenuOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s ease",
              backgroundColor: profileMenuAnchor
                ? isDark
                  ? "rgba(255, 255, 255, 0.1)"
                  : "#f7fafc"
                : "transparent",
              "&:hover": {
                backgroundColor: isDark
                  ? "rgba(255, 255, 255, 0.08)"
                  : "#f7fafc",
                transform: "scale(1.02)",
              },
            }}
          >
            <Box
              component="img"
              src={isDark ? "/white.png" : "/black.png"}
              alt="SportsApp Logo"
              sx={{ width: 40, height: 40, objectFit: "contain", flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: isDark ? "#ffffff" : "#1a202c",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userName}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: isDark ? "#718096" : "#718096",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {userEmail}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleProfileMenuOpen(e);
              }}
              sx={{
                color: isDark ? "#718096" : "#718096",
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(255, 255, 255, 0.1)"
                    : "#edf2f7",
                  color: isDark ? "#ffffff" : "#1976d2",
                },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>

          <Menu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                mt: -1,
                ml: -2,
                minWidth: 200,
                borderRadius: "12px",
                boxShadow: isDark
                  ? "0 10px 40px rgba(0,0,0,0.5)"
                  : "0 10px 40px rgba(0,0,0,0.15)",
                backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
                border: isDark
                  ? "1px solid rgba(255,255,255,0.05)"
                  : "1px solid #e2e8f0",
              },
            }}
          >
            <MuiMenuItem
              onClick={() => {
                navigate("/admin/account");
                handleProfileMenuClose();
              }}
              sx={{
                fontSize: "0.875rem",
                color: isDark ? "#ffffff" : "#1a202c",
                py: 1.5,
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(255, 255, 255, 0.05)"
                    : "#f7fafc",
                },
              }}
            >
              <SettingsIcon
                sx={{
                  fontSize: 18,
                  mr: 1.5,
                  color: isDark ? "#818cf8" : "#1976d2",
                }}
              />
              Account Settings
            </MuiMenuItem>
            <Divider
              sx={{
                borderColor: isDark
                  ? "rgba(255, 255, 255, 0.05)"
                  : "#e2e8f0",
                my: 0.5,
              }}
            />
            <MuiMenuItem
              onClick={handleLogout}
              sx={{
                fontSize: "0.875rem",
                color: "#ef4444",
                py: 1.5,
                "&:hover": {
                  backgroundColor: isDark
                    ? "rgba(239, 68, 68, 0.1)"
                    : "#fef2f2",
                },
              }}
            >
              <LogoutIcon sx={{ fontSize: 18, mr: 1.5 }} />
              Logout
            </MuiMenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  profile: state.user.profile,
});

const mapDispatchToProps = (dispatch) => ({
  save_user_data: (data) => dispatch({ type: Types.LOGIN, payload: data }),
});

export default connect(mapStateToProps, mapDispatchToProps)(Sidebar);
